"use client";

import { useBranchesQuery } from "@/modules/branches/presentation/api/queries";
import { formatDateForInput, parseISO } from "@/shared/lib/date-time";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { DatePicker } from "@/shared/ui/DatePicker";

import { useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";

export type DateRangePreset = "today" | "thisWeek" | "thisMonth" | "custom";

export type DashboardFiltersProps = {
  onRefresh?: () => void;
  isLoading?: boolean;
};

function getDateRangeForPreset(preset: DateRangePreset): {
  from: Date;
  to: Date;
} {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  switch (preset) {
    case "today": {
      const to = new Date(today);
      to.setHours(23, 59, 59, 999);
      return { from: today, to };
    }
    case "thisWeek": {
      const from = new Date(today);
      const dayOfWeek = from.getDay();
      const diff = from.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1); // Monday
      from.setDate(diff);
      const to = new Date(today);
      to.setHours(23, 59, 59, 999);
      return { from, to };
    }
    case "thisMonth": {
      const from = new Date(today.getFullYear(), today.getMonth(), 1);
      const to = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      to.setHours(23, 59, 59, 999);
      return { from, to };
    }
    default:
      return { from: today, to: today };
  }
}

export function DashboardFilters({
  onRefresh,
  isLoading,
}: DashboardFiltersProps) {
  const nav = useNavigate({ from: "/app/dashboard" });
  const search = useSearch({ from: "/app/dashboard" }) as {
    dateRangePreset?: DateRangePreset;
    dateFrom?: string;
    dateTo?: string;
    branchId?: string;
  };

  const dateRangePreset = search.dateRangePreset || "today";
  const dateFrom = search.dateFrom;
  const dateTo = search.dateTo;
  const branchId = search.branchId;

  const { data: branchesData } = useBranchesQuery({ limit: 100 });
  const [fromDate, setFromDate] = useState<string>(
    dateFrom || formatDateForInput(getDateRangeForPreset(dateRangePreset).from),
  );
  const [toDate, setToDate] = useState<string>(
    dateTo || formatDateForInput(getDateRangeForPreset(dateRangePreset).to),
  );

  // Sync local state with URL params
  useEffect(() => {
    if (dateFrom) {
      setFromDate(dateFrom);
    } else if (dateRangePreset !== "custom") {
      setFromDate(
        formatDateForInput(getDateRangeForPreset(dateRangePreset).from),
      );
    }
  }, [dateFrom, dateRangePreset]);

  useEffect(() => {
    if (dateTo) {
      setToDate(dateTo);
    } else if (dateRangePreset !== "custom") {
      setToDate(formatDateForInput(getDateRangeForPreset(dateRangePreset).to));
    }
  }, [dateTo, dateRangePreset]);

  const branchOptions = useMemo(() => {
    const options = [{ value: "all", label: "ທຸກສາຂາ" }];
    if (branchesData?.data) {
      branchesData.data.forEach((branch) => {
        options.push({
          value: branch.id,
          label: branch.name || branch.code || branch.id,
        });
      });
    }
    return options;
  }, [branchesData]);

  const handlePresetChange = (preset: DateRangePreset) => {
    if (preset === "custom") {
      nav({
        search: {
          ...search,
          dateRangePreset: preset,
          dateFrom: fromDate || undefined,
          dateTo: toDate || undefined,
        },
      });
      return;
    }
    const { from, to } = getDateRangeForPreset(preset);
    const fromStr = formatDateForInput(from);
    const toStr = formatDateForInput(to);
    setFromDate(fromStr);
    setToDate(toStr);
    nav({
      search: {
        ...search,
        dateRangePreset: preset,
        dateFrom: fromStr,
        dateTo: toStr,
      },
    });
  };

  const handleDateRangeChange = (
    range: { from?: Date; to?: Date } | undefined,
  ) => {
    const from: Date | undefined = range?.from;
    const to: Date | undefined = range?.to;
    const fromStr = from ? formatDateForInput(from) : "";
    const toStr = to ? formatDateForInput(to) : "";
    setFromDate(fromStr);
    setToDate(toStr);
    nav({
      search: {
        ...search,
        dateRangePreset: "custom",
        dateFrom: fromStr || undefined,
        dateTo: toStr || undefined,
      },
    });
  };

  const handleBranchChange = (value: string) => {
    nav({
      search: {
        ...search,
        branchId: value === "all" ? undefined : value,
      },
    });
  };

  return (
    <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex flex-1 flex-wrap items-center gap-2">
        <SimpleSelect
          value={dateRangePreset}
          onValueChange={(val) => handlePresetChange(val as DateRangePreset)}
          placeholder="ຊ່ວງວັນທີ"
          className="!h-8 w-full sm:w-36"
          options={[
            { value: "today", label: "ມື້ນີ້" },
            { value: "thisWeek", label: "ອາທິດນີ້" },
            { value: "thisMonth", label: "ເດືອນນີ້" },
            { value: "custom", label: "ກຳນົດເອງ" },
          ]}
        />

        {dateRangePreset === "custom" && (
          <DatePicker
            mode="range"
            value={{
              from: fromDate ? parseISO(fromDate) : undefined,
              to: toDate ? parseISO(toDate) : undefined,
            }}
            onChange={handleDateRangeChange}
            placeholder="ຊ່ວງວັນທີ"
            className="h-8 w-full sm:w-fit"
          />
        )}

        <SimpleSelect
          value={branchId || "all"}
          onValueChange={handleBranchChange}
          placeholder="ສາຂາ"
          className="!h-8 w-full sm:w-36"
          options={branchOptions}
        />
      </div>
    </div>
  );
}
